import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { mutateCallback } from 'swr/dist/types';

import { useGetSiniestros } from '../hooks/Siniestro/useSiniestros';
import { Siniestro } from '../types/Siniestro/types';
import { usePets } from './PetsContext';

type SiniestrosContextType = {
  siniestros: Siniestro[] | undefined;
  isValidating: boolean;
  error: unknown;
  currentSiniestro: Siniestro | null;
  setCurrentSiniestro: (s: Siniestro | null) => void;
  mutateSiniestros: (
    data?: Siniestro[] | Promise<Siniestro[]> | mutateCallback<Siniestro[]>,
    shouldRevalidate?: boolean,
  ) => Promise<Siniestro[]>;
};

const SiniestrosContext = createContext<SiniestrosContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Context que maneja la lista de siniestros y el siniestro seleccionado actualmente.
 *
 * Este context es liviano (6 dependencias) y solo cambia cuando:
 * - La lista de siniestros se actualiza (fetch, mutate)
 * - El usuario selecciona un siniestro diferente
 * - El estado de validación cambia
 */
export const SiniestrosContextProvider: React.FC<ProviderProps> = ({ children }) => {
  const { currentPet } = usePets();
  const petId = currentPet?.id ?? '';

  const {
    siniestros,
    isValidating,
    error,
    mutate: mutateSiniestros,
  } = useGetSiniestros(String(petId));

  const [currentSiniestro, setCurrentSiniestro] = useState<Siniestro | null>(null);

  // Limpiar el siniestro seleccionado cuando cambia la mascota activa,
  // para evitar que datos de otra mascota queden asociados a la nueva.
  useEffect(() => {
    setCurrentSiniestro(null);
  }, [petId]);

  const contextValue = useMemo(
    () => ({
      siniestros,
      isValidating,
      error,
      currentSiniestro,
      setCurrentSiniestro,
      mutateSiniestros,
    }),
    [siniestros, isValidating, error, currentSiniestro, mutateSiniestros],
  );

  return (
    <SiniestrosContext.Provider value={contextValue}>
      {children}
    </SiniestrosContext.Provider>
  );
};

export const useSiniestros = (): SiniestrosContextType => {
  const context = useContext(SiniestrosContext);
  if (!context) {
    throw new Error('useSiniestros must be used within a SiniestrosContextProvider');
  }
  return context;
};
